/**
 * product-video-autoplay.js
 *
 * Make native-video product media autoplay (muted, looped) in BOTH the
 * main image carousel slot AND its thumbnail tile, without triggering
 * the Vue gallery's media-change handler.
 *
 * Why bypass the click handler:
 *   The Vue gallery renders native-video slides as `preview image +
 *   play button`; clicking the button calls onMediaActivate which
 *   1) mounts the actual <video> markup from the matching <template>
 *   2) sets that media as the active main slide
 *   Step 2 caused the video to "pop to the top" of the gallery on
 *   every page load. Customers wanted product photos first by default.
 *
 *   This script does step 1 manually (clones the template content into
 *   the slide DOM) without doing step 2, so the video plays in place
 *   wherever the gallery already had it.
 *
 * What it does:
 *   1. Main slide: finds the play-button wrapper for each native-video
 *      media, swaps in the template's full <video> markup. Shopify's
 *      `video_tag` filter (see snippets/product-media-templates.liquid)
 *      already emits autoplay/loop/muted/playsinline/controls=false, so
 *      playback starts on its own.
 *   2. Thumbnail: replaces the preview image inside the corresponding
 *      `.product-medias__thumbnail[data-media-id="..."]` with a small
 *      autoplay <video> built from the template's <source> elements so
 *      the thumb tile is a moving loop too.
 *
 * Only native `video` media is touched. `external_video` (YouTube /
 * Vimeo) and `model` media still need an explicit user gesture and
 * keep their preview-image + button.
 */
(function () {
  'use strict';

  // Poll for ~10 seconds total. On slow mobile + cold cache, the Vue
  // gallery + Swiper init can easily take longer than the previous
  // 3-second window — past that the script silently gave up.
  var MAX_ATTEMPTS = 100;
  var INTERVAL_MS = 100;
  // Once Swiper is wired up, keep listening for class changes so we
  // can re-inject if Vue ever blows our video away on re-render. Map
  // of mediaId -> template element so we can rebuild.
  var templatesByMediaId = {};

  function findVideoTemplates() {
    var out = [];
    var templates = document.querySelectorAll('template[id^="media-template-"]');
    for (var i = 0; i < templates.length; i++) {
      var t = templates[i];
      if (!t.content) continue;
      var el = t.content.querySelector('[data-media-type="video"]');
      if (el && el.dataset && el.dataset.mediaId) {
        out.push({ template: t, mediaId: el.dataset.mediaId });
      }
    }
    return out;
  }

  // Grab the best-resolution image URL we can get out of an <img> that
  // Vue's v-responsive directive (or Shopify's responsive-image snippet)
  // has populated. Tries src first, then a candidate from srcset, then
  // data-src as a last resort. Skips placeholder hashes like "#".
  function resolveImgUrl(img) {
    if (!img) return null;
    var src = img.getAttribute('src');
    if (src && src !== '#' && src.indexOf('data:') !== 0) return src;
    var srcset = img.getAttribute('srcset');
    if (srcset) {
      // Pick the first URL from the srcset list.
      var first = srcset.split(',')[0];
      if (first) {
        var url = first.trim().split(/\s+/)[0];
        if (url) return url;
      }
    }
    var dataSrc = img.getAttribute('data-src');
    if (dataSrc && dataSrc !== '#') return dataSrc;
    return null;
  }

  function injectMainSlide(mediaId, template) {
    var btn = document.querySelector(
      '.product-medias__icon[data-media-id="' + mediaId + '"]'
    );
    if (!btn) return false;
    // The button + preview <img> are siblings inside an unclassed wrapper
    // div (see snippets/product-media.liquid line 16: `<div v-if="media.media_type !== 'image'" style="position: relative">`).
    var wrapper = btn.parentElement;
    if (!wrapper) return false;
    // Skip if our video is already there. We intentionally do NOT use a
    // dataset flag as the guard — Vue can re-render the slide and wipe
    // our injected DOM, leaving the flag stale. Checking for the real
    // <video> child lets us re-inject correctly in that case.
    if (wrapper.querySelector('video')) return false;
    // Capture the preview image's URL BEFORE we wipe the wrapper — we
    // pass it to the <video> as its `poster` so on slow connections the
    // viewer sees the first-frame still while the video bytes load,
    // instead of a black flash. video_tag doesn't emit poster on its
    // own, so we set it on the cloned element.
    var posterUrl = resolveImgUrl(wrapper.querySelector('img'));
    wrapper.innerHTML = '';
    var fragment = template.content.cloneNode(true);
    if (posterUrl) {
      var v = fragment.querySelector('video');
      if (v) v.setAttribute('poster', posterUrl);
    }
    wrapper.appendChild(fragment);

    // Belt-and-suspenders attribute setting on the cloned <video>. iOS
    // Safari is picky about muted+playsinline being present BEFORE the
    // element is inserted into the DOM (it already is via the template,
    // but we re-affirm here for any clone the template machinery may
    // strip). Also force `preload: auto` so the file starts buffering
    // immediately — without it, hitting the slide can show a frozen
    // poster for seconds while bytes arrive.
    var videoEl = wrapper.querySelector('video');
    if (videoEl) {
      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');
      videoEl.setAttribute('autoplay', '');
      videoEl.setAttribute('loop', '');
      videoEl.setAttribute('preload', 'auto');
    }

    // The Shopify video template wraps the <video> in a div with an
    // inline `padding-top: <aspect>%` (classic aspect-ratio trick,
    // see snippets/product-media-templates.liquid line 48). That trick
    // depends on the <video> being position: absolute inside the
    // padded box. But our custom.css sizes the video by `height: auto`
    // on mobile / `height: 100%` on desktop — so the padding-top
    // becomes pure empty space above the video. Strip it.
    var aspectWrapper = wrapper.querySelector('.product-single__media__wrapper');
    if (aspectWrapper) aspectWrapper.style.paddingTop = '0';
    return true;
  }

  // Bind every video slide's play/pause lifecycle to the Swiper
  // instance directly. Swiper attaches itself to its container as
  // `container.swiper` (see assets/theme.js line 41315), so once
  // that property exists we can subscribe to `slideChange` with
  // first-class confidence — no class-watching, no
  // IntersectionObserver flakiness.
  //
  // We retry for a while because on mobile our injection code can
  // run *before* the Vue ProductMedia component finishes `mounted()`
  // and instantiates Swiper.
  function bindSwiperLifecycle() {
    var container = document.querySelector(
      '.product-images-container .swiper-container'
    );
    if (!container) return false;
    var swiper = container.swiper;
    if (!swiper) return false;
    if (container.dataset.rpVideoSwiperBound) return true;
    container.dataset.rpVideoSwiperBound = '1';

    var sync = function () {
      // Find every <video> in the swiper-wrapper and play the one
      // inside the active slide, pause the rest (resetting to 0 so
      // re-entry feels fresh).
      var slides = container.querySelectorAll('.swiper-slide');
      slides.forEach(function (slide) {
        var video = slide.querySelector('video');
        if (!video) return;
        if (slide.classList.contains('swiper-slide-active')) {
          tryPlay(video);
        } else {
          if (!video.paused) {
            try { video.pause(); } catch (e) {}
          }
          try { video.currentTime = 0; } catch (e) {}
        }
      });
    };

    swiper.on('slideChange', sync);
    swiper.on('slideChangeTransitionEnd', sync);
    swiper.on('init', sync);
    // Fire once now — slideChange won't have fired for the slide
    // Swiper picked at init time.
    sync();
    return true;
  }

  function tryPlay(video) {
    // Defensive: iOS requires these to be true at the moment play()
    // is called. We already set them at inject time, but Vue
    // re-renders or attribute scrubbing could undo that.
    video.muted = true;
    video.playsInline = true;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  // Vue can re-render the swiper-wrapper's children (e.g. when
  // `activeMedia` changes) and clobber our injected <video>, putting
  // the play-button overlay back. Watch for that and re-inject.
  function watchForReinject() {
    var wrapper = document.querySelector(
      '.product-images-container .swiper-wrapper'
    );
    if (!wrapper || wrapper.dataset.rpVideoWatchBound) return;
    wrapper.dataset.rpVideoWatchBound = '1';
    var mo = new MutationObserver(function () {
      // Re-run injection for any media whose video disappeared.
      Object.keys(templatesByMediaId).forEach(function (mediaId) {
        injectMainSlide(mediaId, templatesByMediaId[mediaId]);
      });
      // Also re-sync play state in case the active slide swapped.
      bindSwiperLifecycle();
    });
    mo.observe(wrapper, { childList: true, subtree: true });
  }

  function injectThumb(mediaId, template) {
    var thumb = document.querySelector(
      '.product-medias__thumbnail[data-media-id="' + mediaId + '"]'
    );
    if (!thumb || thumb.dataset.rpVideoInjected) return false;
    // Pull the <source> children out of the template's <video> so we can
    // mirror them into a thumb-sized autoplay <video>.
    var tplVideo = template.content.querySelector('video');
    if (!tplVideo) return false;
    var sources = tplVideo.querySelectorAll('source');
    if (sources.length === 0) return false;

    // Capture the thumb's preview image URL before we wipe it so we can
    // set it as the video poster (avoids a black flash on slow loads).
    var posterUrl = resolveImgUrl(thumb.querySelector('img'));

    thumb.dataset.rpVideoInjected = '1';

    var video = document.createElement('video');
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.controls = false;
    video.preload = 'metadata';
    if (posterUrl) video.setAttribute('poster', posterUrl);
    // Match the responsive-image styles the thumbnail had before:
    // fill the tile, cover-crop so the loop reads at a glance.
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
    video.style.display = 'block';

    for (var i = 0; i < sources.length; i++) {
      var src = sources[i];
      var clone = document.createElement('source');
      var url = src.getAttribute('src');
      var type = src.getAttribute('type');
      if (url) clone.setAttribute('src', url);
      if (type) clone.setAttribute('type', type);
      video.appendChild(clone);
    }

    // Clear ALL existing thumb children — including the .product-medias__emblem
    // play-icon SVG. The looping video itself is the visual cue that this
    // tile represents a video; the play icon on top of a moving loop is
    // visual noise.
    thumb.innerHTML = '';
    thumb.appendChild(video);
    // Defensive: some browsers don't honor the autoplay attribute when
    // the element is inserted via JS — call .play() explicitly. Wrap in
    // a catch so an autoplay rejection (rare on muted videos) doesn't
    // crash the rest of the script.
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
    return true;
  }

  function activate() {
    var entries = findVideoTemplates();
    if (entries.length === 0) return true; // nothing to do, stop polling
    var anyButtonRendered = !!document.querySelector(
      '.product-medias__icon[data-media-id]'
    );
    var anyThumbRendered = !!document.querySelector(
      '.product-medias__thumbnail[data-media-id]'
    );
    // Also treat the page as "ready" if our video is already
    // injected — that means a previous poll did the injection and
    // the button is gone, but we still need to keep polling to
    // bind Swiper events.
    var alreadyInjected = !!document.querySelector(
      '.product-images-container video'
    );
    // Wait for Vue to render the main slides AND the thumb strip before
    // we try to inject — otherwise our query selectors return nothing
    // and we'd never get a chance.
    if (!anyButtonRendered && !anyThumbRendered && !alreadyInjected) return false;

    entries.forEach(function (entry) {
      // Stash for potential re-injection if Vue blows our video away.
      templatesByMediaId[entry.mediaId] = entry.template;
      injectMainSlide(entry.mediaId, entry.template);
      injectThumb(entry.mediaId, entry.template);
    });
    // Start watching for Vue re-renders that would wipe our video.
    watchForReinject();
    // Try to attach to Swiper. Returns false if it isn't initialized
    // yet — we keep polling until it is.
    var bound = bindSwiperLifecycle();
    // Done iff we have at least attempted injection AND we've
    // managed to bind Swiper. Otherwise keep polling so Swiper
    // event hookup actually lands.
    return bound;
  }

  function poll(state) {
    if (activate()) return;
    if (++state.tries < MAX_ATTEMPTS) {
      setTimeout(function () { poll(state); }, INTERVAL_MS);
    }
  }

  function start() {
    poll({ tries: 0 });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
